import { useState } from "react"
import { toast } from "react-toastify"
import { useFormik } from "formik"
import * as Yup from "yup"

import { useTranslation } from "react-i18next"

import { InputField } from "components/inputs/InputField"

import { Transaction } from "services/total/types"

import { Modal } from "./Modal"

type ForceDeclineForm = {
  reason: string
}

type ModalForceDeclineActionProps = {
  data: Transaction | null
  handler: () => void
  actionHandler: (tx: Transaction, reason: string, refund: boolean) => Promise<void>
}

export const ModalForceDeclineAction = ({ data, handler, actionHandler }: ModalForceDeclineActionProps) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const [isSubmitting, setIsSubmitting] = useState(false)

  const formik = useFormik<ForceDeclineForm>({
    initialValues: {
      reason: ""
    },
    onSubmit: () => {},
    validationSchema: Yup.object({
      reason: Yup.string().required("Reason is required")
    })
  })

  const handleForceDecline = async (refund: boolean) => {
    const errors = await formik.validateForm()
    formik.setTouched({ reason: true })
    if (errors.reason || !data) return

    setIsSubmitting(true)
    try {
      await actionHandler(data, formik.values.reason, refund)
      handler()
      formik.resetForm()
    } catch (err: any) {
      toast.error(err?.message || "Failed")
    } finally {
      setIsSubmitting(false)
    }
  }

  const onClose = () => {
    formik.resetForm()
    handler()
  }

  return (
    <Modal open={!!data} handler={onClose}>
      <div className="relative flex flex-col justify-between modal-box max-w-[1000px] w-auto">
        <h3 className="text-lg font-bold pb-2">{t("modal.forceDeclineTitle")}</h3>
        <p className="pb-4 text-sm opacity-70">{t("modal.forceDeclineDescription")}</p>
        <InputField
          id="reason"
          title={t("modal.reason")}
          value={formik.values.reason}
          onChange={formik.handleChange}
          invalid={!!(formik.errors.reason && formik.touched.reason)}
          altLabelTwo={formik.touched.reason ? formik.errors.reason : ""}
        />
        <div className="modal-action flex flex-col gap-2 sm:flex-row">
          <button className="btn btn-secondary" onClick={onClose} type="button" disabled={isSubmitting}>
            {t("common.cancel")}
          </button>
          <button className="btn btn-error btn-outline" onClick={() => handleForceDecline(false)} type="button" disabled={isSubmitting}>
            {t("modal.declineNoRefund")}
          </button>
          <button className="btn btn-error" onClick={() => handleForceDecline(true)} type="button" disabled={isSubmitting}>
            {t("modal.declineRefund")}
          </button>
        </div>
      </div>
    </Modal>
  )
}
